import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApolloQueryResult } from '@apollo/client/core';
import { FetchPolicy } from '@core/enums/fetch-policy-apollo.enum';
import {
  GetEstimationTimeSlotsDetails,
  GetListRequestTimeWindow,
  GetTimeSlotsEstimationStatusFilters,
  RequestTimeWindowFilter,
} from '@core/interfaces/requests-time-windows.interface';
import { ILocationFilter } from '@core/interfaces/time-window.interface';
import { environment } from '@environments/environment';
import { Apollo, gql, MutationResult } from 'apollo-angular';
import dayjs from 'dayjs';
import { Observable } from 'rxjs';
import {
  TimeSlotsCalculationEmulationResponse,
  CreateCartEstimation,
} from '@core/interfaces/create-time-slots-estimation.interface';
import { GetListParams } from '@core/interfaces/get-list.interface';

@Injectable({
  providedIn: 'root',
})
export class RequestsTimeWindowsService {
  constructor(
    private readonly apollo: Apollo,
    private readonly http: HttpClient,
  ) {}

  public exportTimeSlotsEstimation(filter: RequestTimeWindowFilter, locationFilter: ILocationFilter): Observable<Blob> {
    let params = new HttpParams();

    if (filter.createdAt?.from) {
      params = params.set('createdAt[from]', dayjs(filter.createdAt.from).toISOString());
    }

    if (filter.createdAt?.to) {
      params = params.set('createdAt[to]', dayjs(filter.createdAt.to).toISOString());
    }

    if (locationFilter.zoneId != null) {
      params = params.set('zoneId', locationFilter.zoneId.toString());
    }

    if (locationFilter.cityId != null) {
      params = params.set('cityId', locationFilter.cityId.toString());
    }

    if (locationFilter.regionId != null) {
      params = params.set('regionId', locationFilter.regionId.toString());
    }

    if (locationFilter.countryId != null) {
      params = params.set('countryId', locationFilter.countryId.toString());
    }

    return this.http.get(`${environment.serverUrl}time-slots-estimation/list/export`, {
      params,
      responseType: 'blob',
    });
  }

  public getListRequestTimeWindow(
    params: GetListParams<RequestTimeWindowFilter>,
  ): Observable<ApolloQueryResult<GetListRequestTimeWindow>> {
    return this.apollo.query<GetListRequestTimeWindow>({
      query: gql`
        query getListTimeSlotsEstimation(
          $filter: TimeSlotsEstimationsFilterDto
          $pagination: OffsetPaginationInput
          $sort: TimeSlotsEstimationsSortInput
        ) {
          getListTimeSlotsEstimation(filter: $filter, pagination: $pagination, sort: $sort) {
            result {
              cartId
              carts {
                fullReplacements {
                  items {
                    productId
                    quantity
                  }
                  timeSlots {
                    routeListId
                    timeFrom
                    timeTo
                    workScheduleId
                  }
                }
                main {
                  items {
                    productId
                    quantity
                  }
                  timeSlots {
                    routeListId
                    timeFrom
                    timeTo
                    workScheduleId
                  }
                }
                partialReplacements {
                  items {
                    productId
                    quantity
                  }
                  timeSlots {
                    routeListId
                    timeFrom
                    timeTo
                    workScheduleId
                  }
                }

                ipost {
                  items {
                    productId
                    quantity
                  }
                  timeSlots {
                    routeListId
                    timeFrom
                    timeTo
                    workScheduleId
                  }
                }

                reducedQty {
                  items {
                    productId
                    quantity
                  }
                  timeSlots {
                    routeListId
                    timeFrom
                    timeTo
                    workScheduleId
                  }
                }
              }
              originalCart {
                items {
                  measurementType
                  price
                  product {
                    article
                    name
                  }
                  quantity
                }
              }
              createdAt
              deliveryAddress
              error
              id
              initialOrder {
                extOrderId
              }
              logs
              originalCartValue
              requestData {
                requestSourceName
              }
              resultedCarts {
                fullReplacements {
                  measurementType
                  price
                  product {
                    article
                    name
                  }
                  quantity
                }
                main {
                  measurementType
                  price
                  product {
                    id
                    name
                    article
                  }
                  quantity
                }
                partialReplacements {
                  measurementType
                  price
                  product {
                    article
                    name
                  }
                  quantity
                }
                reducedQty {
                  measurementType
                  price
                  product {
                    id
                    name
                    article
                  }
                  quantity
                }
              }
            }
            total
          }
        }
      `,
      variables: {
        filter: {
          ...params.filter,
        },
        pagination: params.pagination,
        sort: params.sort,
      },
      fetchPolicy: FetchPolicy.NoCache,
    });
  }

  public getTimeSlotsEstimationStatusFilters(): Observable<ApolloQueryResult<GetTimeSlotsEstimationStatusFilters>> {
    return this.apollo.query<GetTimeSlotsEstimationStatusFilters>({
      query: gql`
        query getTimeSlotsEstimationStatusFilters {
          getTimeSlotsEstimationStatusFilters {
            key
            label
          }
        }
      `,
      fetchPolicy: FetchPolicy.NoCache,
    });
  }

  public getEstimationTimeSlotsDetails(id: number): Observable<ApolloQueryResult<GetEstimationTimeSlotsDetails>> {
    return this.apollo.query<GetEstimationTimeSlotsDetails>({
      query: gql`
        query EstimationTimeSlotsDetails($id: Int!) {
          estimationTimeSlotsDetails(id: $id) {
            id
            cartId
            calculatedAt
            orderSource
            requestSourceName
            deliveryProcessType
            orderProcessType
            deliveryZoneId
            deliveryZoneName
            destinationAddress
            deliveryPointLat
            deliveryPointLng
            destinationType
            color
            phraseKey
            restrictions
            basePharmacy {
              pharmacyId
              pharmacyName
              pharmacyLegalName
              zoneId
            }
            mainDeliveryData {
              success
              failureReasonKey
              sourcePharmacy {
                pharmacyId
                pharmacyName
                pharmacyLegalName
                zoneId
                isBasePharmacy
                selectionMethod
              }
              transitChain {
                pharmacyId
                pharmacyName
                pharmacyLegalName
                zoneId
              }
              pharmacyTransfer {
                fromPharmacy {
                  pharmacyId
                  pharmacyName
                  pharmacyLegalName
                  zoneId
                  isBasePharmacy
                  selectionMethod
                }
                toPharmacy {
                  pharmacyId
                  pharmacyName
                  pharmacyLegalName
                  zoneId
                  isBasePharmacy
                  selectionMethod
                }
                transitChain {
                  pharmacyId
                  pharmacyName
                  pharmacyLegalName
                  zoneId
                }
              }
              searchSteps {
                step
                outcome
                pharmacies {
                  pharmacyId
                  pharmacyName
                  pharmacyLegalName
                }
              }
              failureDetails {
                reasonKey
                failedAtStep
                missingItems {
                  requestProductId
                  resolvedProductId
                  vendorCode
                  crmId
                  partnerId
                }
              }
            }
            alternativeDeliveryData {
              activeMode
              status
              sourcePharmacy {
                pharmacyId
                pharmacyName
                pharmacyLegalName
                zoneId
                isBasePharmacy
                selectionMethod
              }
              searchSteps {
                step
                outcome
                pharmacies {
                  pharmacyId
                  pharmacyName
                  pharmacyLegalName
                }
              }
              missingItems {
                requestProductId
                resolvedProductId
                vendorCode
                crmId
                partnerId
              }
            }
          }
        }
      `,
      variables: { id },
      fetchPolicy: FetchPolicy.NoCache,
    });
  }

  public emulateTimeSlotsCalculation(
    data: CreateCartEstimation,
  ): Observable<MutationResult<{ emulateTimeSlotsCalculation: TimeSlotsCalculationEmulationResponse }>> {
    return this.apollo.mutate<{ emulateTimeSlotsCalculation: TimeSlotsCalculationEmulationResponse }>({
      mutation: gql`
        mutation emulateTimeSlotsCalculation($data: TimeSlotsCalculationEmulationDto!) {
          emulateTimeSlotsCalculation(data: $data) {
            status
            requestId
          }
        }
      `,
      variables: {
        data,
      },
    });
  }
}
